import { createContext, lazy, Suspense, useContext, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button, Spin } from 'antd';
import { BellSlashIcon } from '@phosphor-icons/react';
import { useHubAlertSetup, type HubAlertSetup } from './useHubAlertSetup';

const HubAlertSetupPage = lazy(() => import('./HubAlertSetupPage'));

export const SETUP_PATH = '/alerts-setup';

const SetupContext = createContext<HubAlertSetup | null>(null);

/** One live readiness check for the page area and the header (gate, strip, alert status). */
export const HubAlertSetupProvider = ({ children }: { children: ReactNode }) => (
  <SetupContext.Provider value={useHubAlertSetup()}>{children}</SetupContext.Provider>
);

export const useSharedHubAlertSetup = () => {
  const setup = useContext(SetupContext);
  if (!setup) throw new Error('useSharedHubAlertSetup needs HubAlertSetupProvider');
  return setup;
};

/** Soft mode: one slim red line on every Hub page until this device is set up. */
export const HubAlertSetupStrip = ({ onOpen }: { onOpen: () => void }) => (
  <div
    role="status"
    onClick={onOpen}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap',
      padding: '8px 16px',
      background: '#B42318',
      color: '#fff',
      cursor: 'pointer',
    }}
  >
    <BellSlashIcon size={20} weight="bold" aria-hidden />
    <span style={{ flex: 1, minWidth: 200, fontWeight: 600 }}>
      Order alerts are not set up on this device. You may miss new orders.
    </span>
    <Button
      size="small"
      onClick={event => {
        event.stopPropagation();
        onOpen();
      }}
      style={{ color: '#B42318', fontWeight: 700 }}
    >
      Set up now
    </Button>
  </div>
);

/**
 * Wraps Hub's page area. Soft mode: the strip above every page. Hard mode: the setup page instead
 * of any page until this device is ready (the header, with logout, stays). Alerts keep running.
 */
export const HubAlertSetupGate = ({ children }: { children: ReactNode }) => {
  const setup = useSharedHubAlertSetup();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (setup.holding) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (setup.gate) {
    return (
      <Suspense fallback={<Spin style={{ display: 'block', margin: 48 }} />}>
        <HubAlertSetupPage variant="gate" />
      </Suspense>
    );
  }
  return (
    <>
      {setup.showStrip && pathname !== SETUP_PATH && (
        <HubAlertSetupStrip onOpen={() => navigate(SETUP_PATH)} />
      )}
      {children}
    </>
  );
};
